import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Armchair, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Home,
  Sofa,
  Lamp,
  TreeDeciduous,
  Ruler,
  Palette,
  Square,
  Table,
  DoorOpen,
  BookOpen,
  Frame,
  Bath
} from 'lucide-react';
import { toast } from 'sonner';

// Furniture types
const FURNITURE_TYPES = [
  { value: 'tavolo', label: 'Tavolo', icon: Table },
  { value: 'sedia', label: 'Sedie', icon: Armchair },
  { value: 'mobile_contenitore', label: 'Mobile contenitore / Credenza', icon: Square },
  { value: 'libreria', label: 'Libreria / Scaffale', icon: BookOpen },
  { value: 'letto', label: 'Letto / Testiera', icon: Sofa },
  { value: 'armadio', label: 'Armadio / Guardaroba', icon: DoorOpen },
  { value: 'scrivania', label: 'Scrivania / Postazione lavoro', icon: Table },
  { value: 'divano', label: 'Divano / Poltrona', icon: Sofa },
  { value: 'consolle', label: 'Consolle / Mobile ingresso', icon: Square },
  { value: 'mobile_bagno', label: 'Mobile bagno', icon: Bath },
  { value: 'illuminazione', label: 'Lampada / Illuminazione', icon: Lamp },
  { value: 'complemento', label: 'Complemento d\'arredo', icon: Frame },
  { value: 'altro', label: 'Altro', icon: Home },
];

// Material preferences
const MATERIALS = [
  { value: 'legno_massello', label: 'Legno massello', icon: TreeDeciduous },
  { value: 'legno_noce', label: 'Noce', icon: TreeDeciduous },
  { value: 'legno_rovere', label: 'Rovere', icon: TreeDeciduous },
  { value: 'legno_castagno', label: 'Castagno', icon: TreeDeciduous },
  { value: 'legno_ulivo', label: 'Ulivo', icon: TreeDeciduous },
  { value: 'legno_recupero', label: 'Legno di recupero', icon: TreeDeciduous },
  { value: 'ferro_battuto', label: 'Ferro battuto', icon: Square },
  { value: 'metallo', label: 'Metallo / Acciaio', icon: Square },
  { value: 'marmo', label: 'Marmo / Pietra', icon: Square },
  { value: 'vetro', label: 'Vetro', icon: Square },
  { value: 'misto', label: 'Materiali misti', icon: Palette },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Style preferences
const STYLES = [
  { value: 'moderno', label: 'Moderno / Minimal', icon: Square },
  { value: 'industriale', label: 'Industriale', icon: Square },
  { value: 'rustico', label: 'Rustico / Country', icon: TreeDeciduous },
  { value: 'classico', label: 'Classico / Tradizionale', icon: Frame },
  { value: 'vintage', label: 'Vintage / Retrò', icon: Frame },
  { value: 'scandinavo', label: 'Scandinavo / Nordico', icon: Home },
  { value: 'contemporaneo', label: 'Contemporaneo', icon: Sparkles },
  { value: 'mediterraneo', label: 'Mediterraneo', icon: Home },
  { value: 'eclettico', label: 'Eclettico / Mix', icon: Palette },
  { value: 'altro', label: 'Altro stile', icon: Palette },
];

// Room/Space
const ROOMS = [
  { value: 'soggiorno', label: 'Soggiorno / Living', icon: Sofa },
  { value: 'cucina', label: 'Cucina', icon: Home },
  { value: 'camera', label: 'Camera da letto', icon: Home },
  { value: 'studio', label: 'Studio / Ufficio', icon: Table },
  { value: 'ingresso', label: 'Ingresso', icon: DoorOpen },
  { value: 'bagno', label: 'Bagno', icon: Bath },
  { value: 'cameretta', label: 'Cameretta bambini', icon: Home },
  { value: 'esterno', label: 'Esterno / Giardino', icon: TreeDeciduous },
  { value: 'taverna', label: 'Taverna / Cantina', icon: Home },
  { value: 'negozio', label: 'Negozio / Locale commerciale', icon: Home },
  { value: 'altro', label: 'Altro ambiente', icon: Home },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '200-500', label: '€200 - €500', description: 'Complementi semplici' },
  { value: '500-1000', label: '€500 - €1.000', description: 'Piccoli mobili' },
  { value: '1000-2000', label: '€1.000 - €2.000', description: 'Mobile medio' },
  { value: '2000-4000', label: '€2.000 - €4.000', description: 'Mobile importante' },
  { value: '4000-7000', label: '€4.000 - €7.000', description: 'Arredo su misura' },
  { value: '7000-15000', label: '€7.000 - €15.000', description: 'Set completo' },
  { value: '15000+', label: '€15.000+', description: 'Progetto intero ambiente' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1month', label: '1 Mese', description: 'Urgente' },
  { value: '2months', label: '2 Mesi', description: 'Prioritario' },
  { value: '3months', label: '3 Mesi', description: 'Standard' },
  { value: '4-6months', label: '4-6 Mesi', description: 'Con calma' },
  { value: '6months+', label: '6+ Mesi', description: 'Progetto complesso' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example furniture
const FURNITURE_EXAMPLES = [
  {
    idea: "Tavolo da pranzo per 8 in noce massello",
    result: "Tavolo 240x100cm in noce nazionale con gambe tornite e due allunghe nascoste",
    image: "https://images.unsplash.com/photo-1617806118233-18e1de247200?w=400&h=300&fit=crop",
    category: "Tavoli"
  },
  {
    idea: "Libreria a parete stile industriale",
    result: "Libreria in ferro e legno di recupero con 12 ripiani e scala integrata",
    image: "https://images.unsplash.com/photo-1594620302200-9a762244a156?w=400&h=300&fit=crop",
    category: "Librerie"
  },
  {
    idea: "Mobile bagno in legno rustico",
    result: "Mobile sospeso in castagno massello con lavabo integrato e specchio coordinato",
    image: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=300&fit=crop",
    category: "Bagno"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Il tavolo che hanno realizzato è diventato il cuore della nostra casa. Ogni cena è speciale ora.",
    author: "Famiglia Rossi",
    location: "Milano"
  },
  {
    text: "Cercavo una libreria particolare e l'artigiano ha capito esattamente cosa volevo. Qualità straordinaria.",
    author: "Marco B.",
    location: "Torino"
  },
  {
    text: "La credenza su misura entra perfettamente nella nicchia. Impossibile trovarla in negozio!",
    author: "Giulia e Stefano",
    location: "Bologna"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "È gratuito inviare una richiesta?",
    answer: "Sì, è completamente gratuito. Descrivi il mobile che desideri e ricevi proposte da falegnami e artigiani senza alcun costo."
  },
  {
    question: "Sono obbligato ad acquistare?",
    answer: "Assolutamente no. Ricevi le proposte, le valuti con calma e decidi solo se trovi l'artigiano e il progetto giusto per te."
  },
  {
    question: "Gli artigiani vengono a casa per le misure?",
    answer: "Sì, per progetti importanti molti artigiani offrono sopralluoghi gratuiti. Per mobili più semplici, ti guideranno nella presa delle misure."
  },
  {
    question: "Quanto tempo ci vuole per realizzare un mobile?",
    answer: "Dipende dalla complessità. Un complemento semplice può richiedere 3-4 settimane, mentre una cucina completa può richiedere 3-4 mesi."
  },
  {
    question: "Posso vedere il legno prima della lavorazione?",
    answer: "Certamente! Molti artigiani ti invitano in laboratorio per scegliere insieme le tavole di legno più belle per il tuo mobile."
  }
];

export default function ArredamentoRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    furniture_type: '',
    material: '',
    style: '',
    room: '',
    budget: '',
    timeline: '',
    dimensions: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Arredamento su Misura | Falegnami Italiani | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `furniture-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi il mobile che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_furniture_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const furnitureTypeLabel = FURNITURE_TYPES.find(t => t.value === formData.furniture_type)?.label || '';
      const materialLabel = MATERIALS.find(m => m.value === formData.material)?.label || '';
      const styleLabel = STYLES.find(s => s.value === formData.style)?.label || '';
      const roomLabel = ROOMS.find(r => r.value === formData.room)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (furnitureTypeLabel) details.push(`🪑  Tipo mobile: ${furnitureTypeLabel}`);
      if (materialLabel) details.push(`🪵  Materiale: ${materialLabel}`);
      if (styleLabel) details.push(`🎨  Stile: ${styleLabel}`);
      if (roomLabel) details.push(`🏠  Ambiente: ${roomLabel}`);
      if (formData.dimensions) details.push(`📐  Dimensioni: ${formData.dimensions}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${furnitureTypeLabel} in ${materialLabel}`.trim() || 'Mobile su misura',
        description: fullDescription,
        category: 'Arredamento',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artigiani.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('furniture-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Arredamento su Misura | Mobili Artigianali Italiani | Rarifex</title>
        <meta name="description" content="Mobili su misura realizzati da maestri falegnami italiani. Tavoli, librerie, credenze in legno massello. Descrivi il tuo progetto e ricevi proposte personalizzate. Gratuito e senza impegno." />
        <meta name="keywords" content="arredamento su misura, mobili artigianali, falegname italiano, tavolo legno massello, libreria su misura, mobile artigianale, falegnameria made in italy" />
        <link rel="canonical" href="https://www.rarifex.com/arredamento-su-misura" />
        <meta property="og:title" content="Arredamento su Misura | Falegnami Italiani | Rarifex" />
        <meta property="og:description" content="Mobili su misura realizzati da maestri falegnami italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/arredamento-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-orange-50/50 via-white to-stone-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Armchair className="w-4 h-4" />
                    Arredamento su Misura
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    Il mobile{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600">
                      perfetto per casa tua
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri falegnami italiani lo creano su misura per te.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Descrivi il mobile che immagini e ricevi proposte da artigiani del legno esperti.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Falegnami certificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="furniture-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Tavolo da pranzo in rovere per 8 persone"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="furniture-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi il mobile che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Forma, dimensioni, funzionalità speciali, finiture. Più dettagli dai, meglio gli artigiani capiranno la tua visione."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="furniture-description-textarea"
                        />
                      </div>
                      
                      {/* Furniture Type & Material */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tipo di mobile
                          </Label>
                          <select
                            value={formData.furniture_type}
                            onChange={(e) => handleInputChange('furniture_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            data-testid="furniture-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {FURNITURE_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Materiale preferito
                          </Label>
                          <select
                            value={formData.material}
                            onChange={(e) => handleInputChange('material', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            data-testid="furniture-material-select"
                          >
                            <option value="">Seleziona...</option>
                            {MATERIALS.map(mat => (
                              <option key={mat.value} value={mat.value}>{mat.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Style & Room */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Stile</Label>
                          <select
                            value={formData.style}
                            onChange={(e) => handleInputChange('style', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            data-testid="furniture-style-select"
                          >
                            <option value="">Seleziona...</option>
                            {STYLES.map(style => (
                              <option key={style.value} value={style.value}>{style.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Per quale ambiente?</Label>
                          <select
                            value={formData.room}
                            onChange={(e) => handleInputChange('room', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            data-testid="furniture-room-select"
                          >
                            <option value="">Seleziona...</option>
                            {ROOMS.map(room => (
                              <option key={room.value} value={room.value}>{room.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Dimensions */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Dimensioni indicative (opzionale)
                        </Label>
                        <Input
                          placeholder="Es: Larghezza 200cm, profondità 90cm, altezza 75cm"
                          value={formData.dimensions}
                          onChange={(e) => handleInputChange('dimensions', e.target.value)}
                          className="mt-1.5"
                          data-testid="furniture-dimensions-input"
                        />
                      </div>
                      
                      {/* Budget & Timeline */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            data-testid="furniture-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            data-testid="furniture-timeline-select"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di ispirazione (consigliate!)
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-orange-300 hover:bg-orange-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="furniture-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica foto o schizzi ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-lg shadow-orange-200"
                        data-testid="furniture-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Armchair className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                Il mobile perfetto non esiste nei negozi
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-orange-400">✗</span>
                  Le misure standard non entrano mai nello spazio che hai
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-orange-400">✗</span>
                  I materiali sono sempre truciolare o MDF
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-orange-400">✗</span>
                  Vuoi qualcosa di unico, non l'ennesimo mobile Ikea
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                Per un mobile che duri generazioni, serve un falegname.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Un mobile{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-600">
                  fatto apposta per te
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Falegnami e artigiani del legno pronti a realizzare la tua idea.
                <br />
                <strong className="text-slate-800">Tu disegni, loro costruiscono.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Descrivi il tuo mobile</h3>
                  <p className="text-slate-600">Racconta cosa ti serve, misure e stile desiderato</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Falegnami ti propongono</h3>
                  <p className="text-slate-600">Ricevi rendering, preventivi e proposte dettagliate</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli il migliore</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti convince</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a mobile
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno realizzato i nostri falegnami
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {FURNITURE_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-orange-500 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-orange-200 text-orange-600 hover:bg-orange-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-orange-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 3 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Descrivi il tuo mobile</h3>
                    <p className="text-slate-600">
                      Racconta cosa ti serve: tipo di mobile, dimensioni, materiale, stile. Allega foto di ispirazione o schizzi.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-amber-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi proposte</h3>
                    <p className="text-slate-600">
                      Falegnami e artigiani del legno vedono la tua richiesta e ti inviano progetti, rendering e preventivi.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Scegli il tuo artigiano</h3>
                    <p className="text-slate-600">
                      Confronta le proposte, visita il laboratorio se vuoi, affina i dettagli. Nessun obbligo, scegli solo se sei soddisfatto.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché scegliere l'arredamento artigianale
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-orange-50">
                  <CheckCircle className="w-8 h-8 text-orange-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Su misura perfetta</h3>
                    <p className="text-sm text-slate-600">Entra esattamente nello spazio che hai</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-amber-50">
                  <Sparkles className="w-8 h-8 text-amber-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Legno vero massello</h3>
                    <p className="text-sm text-slate-600">Materiali di qualità che durano generazioni</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                  <Shield className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Pezzo unico</h3>
                    <p className="text-sm text-slate-600">Nessuno avrà mai lo stesso mobile</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo di acquisto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i clienti
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center font-semibold text-orange-600">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`furniture-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-orange-50 to-amber-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Armchair className="w-16 h-16 text-orange-500 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronto a creare il tuo mobile?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                I maestri falegnami italiani aspettano la tua idea.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white shadow-lg shadow-orange-200"
                data-testid="furniture-cta-final-button"
              >
                <Armchair className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dai falegnami, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/arredamento-su-misura')}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
